import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Pencil, Trash2, AlertTriangle, CheckCircle2, Info, FlaskConical,
  GitCompare, ArrowRight, TrendingDown, TrendingUp, Minus,
} from "lucide-react";

const SEV = {
  alerta: { cls: "border-destructive bg-destructive/10 text-destructive", Icon: AlertTriangle, label: "Alerta" },
  atencao: { cls: "border-amber-500 bg-amber-500/10 text-amber-800", Icon: AlertTriangle, label: "Atenção" },
  info: { cls: "border-primary bg-primary/5 text-primary", Icon: Info, label: "Info" },
};

const emptyForm = (catalog) => {
  const physical = { consistencia: "", forma: "", cor: "", cheiro: "", viscosidade: "", ph: "" };
  const microscopic = {};
  (catalog?.microscopic || []).forEach((m) => { microscopic[m.id] = ""; });
  return {
    exam_date: new Date().toISOString().slice(0, 10),
    physical,
    microscopic,
    observations: "",
  };
};

// ─────────────────────── Form modal (create/edit) ───────────────────────
function CoproFormModal({ catalog, initial, onClose, onSaved, patientId }) {
  const [form, setForm] = useState(() => initial ? {
    exam_date: initial.exam_date || new Date().toISOString().slice(0, 10),
    physical: { ...emptyForm(catalog).physical, ...(initial.physical || {}) },
    microscopic: { ...emptyForm(catalog).microscopic, ...(initial.microscopic || {}) },
    observations: initial.observations || "",
  } : emptyForm(catalog));
  const [saving, setSaving] = useState(false);

  const setPhys = (k, v) => setForm({ ...form, physical: { ...form.physical, [k]: v } });
  const setMic = (k, v) => setForm({ ...form, microscopic: { ...form.microscopic, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        exam_date: form.exam_date,
        physical: {
          ...form.physical,
          ph: form.physical.ph === "" || form.physical.ph === null ? null : Number(form.physical.ph),
        },
        microscopic: form.microscopic,
        observations: form.observations,
      };
      if (initial?.id) {
        await api.patch(`/admin/coprological-exams/${initial.id}`, payload);
        toast.success("Exame atualizado.");
      } else {
        await api.post(`/admin/patients/${patientId}/coprological-exams`, payload);
        toast.success("Exame criado.");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const phField = catalog?.ph_reference || { min: 6.5, max: 7.5 };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="copro-form-modal"
    >
      <div
        className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-accent mb-1">Prova Coprológica Funcional</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">
              {initial ? "Editar exame" : "Novo exame"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="close-copro-form">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Header row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="overline text-muted-foreground mb-1 block">Data do exame *</label>
              <input
                type="date"
                value={form.exam_date}
                onChange={(e) => setForm({ ...form, exam_date: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                data-testid="copro-exam-date"
              />
            </div>
          </div>

          {/* Análise Física */}
          <section>
            <div className="overline text-muted-foreground mb-2">Análise Física</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(catalog?.physical || []).map((f) => (
                <div key={f.id}>
                  <label className="text-sm font-medium mb-1 block">{f.label}</label>
                  <select
                    value={form.physical[f.id] || ""}
                    onChange={(e) => setPhys(f.id, e.target.value)}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                    data-testid={`copro-phys-${f.id}`}
                  >
                    <option value="">—</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}{o === f.expected ? " (esperado)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  pH <span className="text-xs text-muted-foreground">(esperado {phField.min}–{phField.max})</span>
                </label>
                <input
                  type="number" step="0.1"
                  value={form.physical.ph}
                  onChange={(e) => setPhys("ph", e.target.value)}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                  data-testid="copro-phys-ph"
                />
              </div>
            </div>
          </section>

          {/* Análise Microscópica */}
          <section>
            <div className="overline text-muted-foreground mb-2">Análise Microscópica</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(catalog?.microscopic || []).map((m) => {
                const v = form.microscopic[m.id] || "";
                const alarm = m.expected === "ausente" && v === "Presente";
                return (
                  <div
                    key={m.id}
                    className={`flex items-center justify-between rounded-sm border px-3 py-2 gap-3 ${
                      alarm ? "border-destructive bg-destructive/5" : "border-border"
                    }`}
                    data-testid={`copro-micro-row-${m.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{m.label}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {m.expected === "ausente" ? "Esperado: Ausente" : "Ausente ou Presente (sem alerta)"}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-none">
                      {(catalog?.micro_options || ["Ausente", "Presente"]).map((o) => (
                        <button
                          key={o}
                          type="button"
                          onClick={() => setMic(m.id, o)}
                          className={`px-2.5 py-1 rounded-sm border text-xs font-medium transition-colors ${
                            v === o
                              ? o === "Presente"
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-slate-100 text-slate-700 border-slate-300"
                              : "bg-card border-border hover:bg-muted"
                          }`}
                          data-testid={`copro-micro-${m.id}-${o.toLowerCase()}`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Observações */}
          <section>
            <label className="overline text-muted-foreground mb-1 block">Observações do farmacêutico</label>
            <textarea
              value={form.observations}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
              placeholder="Anotações livres, correlações, próximas condutas…"
              data-testid="copro-observations"
            />
          </section>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
              data-testid="copro-cancel-btn"
            >
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
              data-testid="copro-save-btn"
            >
              <Plus className="w-4 h-4" /> {saving ? "Salvando…" : (initial ? "Salvar alterações" : "Criar exame")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────── Exam card (list item) ───────────────────────
function CoproExamCard({ exam, catalog, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const phys = exam.physical || {};
  const micro = exam.microscopic || {};
  const unex = exam.unexpected || { physical: [], microscopic: [] };
  const interpretations = exam.interpretations || [];

  const dt = exam.exam_date ? new Date(exam.exam_date).toLocaleDateString("pt-BR") : "—";

  const phField = catalog?.ph_reference || { min: 6.5, max: 7.5 };

  return (
    <div className="rounded-sm border border-border bg-card" data-testid={`copro-card-${exam.id}`}>
      <div className="flex items-start justify-between gap-3 p-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <FlaskConical className="w-4 h-4 text-primary" />
            <span className="font-heading font-semibold">Prova Coprológica — {dt}</span>
            {interpretations.length > 0 && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-destructive/40 bg-destructive/10 text-destructive text-[10px] uppercase font-semibold tracking-wider"
                data-testid={`copro-alerts-count-${exam.id}`}
              >
                <AlertTriangle className="w-3 h-3" />
                {interpretations.length} {interpretations.length > 1 ? "sinalizações" : "sinalização"}
              </span>
            )}
            {interpretations.length === 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 text-[10px] uppercase font-semibold tracking-wider">
                <CheckCircle2 className="w-3 h-3" /> Sem alertas
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Consistência {phys.consistencia || "—"} · Cor {phys.cor || "—"} · pH {phys.ph ?? "—"}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-none">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs px-2 py-1 rounded-sm border border-border hover:bg-muted transition-colors"
            data-testid={`copro-expand-${exam.id}`}
          >
            {expanded ? "Ocultar" : "Detalhes"}
          </button>
          <button
            onClick={() => onEdit(exam)}
            className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors"
            title="Editar exame"
            data-testid={`copro-edit-${exam.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(exam)}
            className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
            title="Excluir exame"
            data-testid={`copro-delete-${exam.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 space-y-4" data-testid={`copro-detail-${exam.id}`}>
          {/* Interpretations */}
          {interpretations.length > 0 && (
            <section>
              <div className="overline text-muted-foreground mb-2">Interpretação automática</div>
              <ul className="space-y-2">
                {interpretations.map((i) => {
                  const s = SEV[i.severity] || SEV.info;
                  const Icon = s.Icon;
                  return (
                    <li
                      key={i.rule_id}
                      className={`rounded-sm border ${s.cls} p-3`}
                      data-testid={`copro-interp-${i.rule_id}`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon className="w-4 h-4 flex-none mt-0.5" />
                        <div>
                          <div className="font-semibold text-sm">{i.title}</div>
                          <div className="text-xs mt-1 opacity-90">{i.message}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Physical & microscopic tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <section>
              <div className="overline text-muted-foreground mb-2">Análise Física</div>
              <dl className="text-sm divide-y divide-border border border-border rounded-sm">
                {(catalog?.physical || []).map((f) => {
                  const v = phys[f.id];
                  const unexpected = unex.physical.includes(f.id);
                  return (
                    <div
                      key={f.id}
                      className={`flex justify-between px-3 py-2 gap-2 ${unexpected ? "bg-destructive/5" : ""}`}
                    >
                      <dt className="text-muted-foreground">{f.label}</dt>
                      <dd className={`font-medium ${unexpected ? "text-destructive" : ""}`}>
                        {v || "—"}
                      </dd>
                    </div>
                  );
                })}
                <div className={`flex justify-between px-3 py-2 gap-2 ${unex.physical.includes("ph") ? "bg-destructive/5" : ""}`}>
                  <dt className="text-muted-foreground">pH ({phField.min}–{phField.max})</dt>
                  <dd className={`font-medium ${unex.physical.includes("ph") ? "text-destructive" : ""}`}>
                    {phys.ph ?? "—"}
                  </dd>
                </div>
              </dl>
            </section>

            <section>
              <div className="overline text-muted-foreground mb-2">Análise Microscópica</div>
              <ul className="text-sm divide-y divide-border border border-border rounded-sm">
                {(catalog?.microscopic || []).map((m) => {
                  const v = micro[m.id] || "";
                  const bad = m.expected === "ausente" && v === "Presente";
                  return (
                    <li
                      key={m.id}
                      className={`flex justify-between px-3 py-2 gap-2 ${bad ? "bg-destructive/5" : ""}`}
                    >
                      <span className="text-muted-foreground">{m.label}</span>
                      <span className={`font-medium ${bad ? "text-destructive" : ""}`}>
                        {v || "—"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>

          {exam.observations && (
            <section>
              <div className="overline text-muted-foreground mb-1">Observações</div>
              <p className="text-sm text-foreground/90">{exam.observations}</p>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Compare Modal ───────────────────────
function InterpItem({ interp, tone }) {
  const s = SEV[interp?.severity] || SEV.info;
  const cls =
    tone === "improved"
      ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-900"
      : tone === "worsened"
      ? "border-destructive/40 bg-destructive/5 text-destructive"
      : "border-amber-500/30 bg-amber-500/5 text-amber-900";
  return (
    <li className={`rounded-sm border ${cls} p-3`} data-testid={`compare-interp-${tone}-${interp?.rule_id}`}>
      <div className="text-sm font-semibold">{interp?.title || interp?.pattern_name || interp?.rule_id}</div>
      {interp?.message && <div className="text-xs mt-1 opacity-90">{interp.message}</div>}
    </li>
  );
}

function ChangeArrow({ changed, worse, better }) {
  if (!changed) return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
  if (better) return <TrendingDown className="w-3.5 h-3.5 text-emerald-600" title="Melhora" />;
  if (worse) return <TrendingUp className="w-3.5 h-3.5 text-destructive" title="Piora" />;
  return <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />;
}

function CompareModal({ patientId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(
          `/admin/patients/${patientId}/coprological-exams/compare`
        );
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Erro ao carregar comparação.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [patientId]);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="copro-compare-modal"
    >
      <div
        className="bg-card border border-border rounded-sm p-6 max-w-4xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-accent mb-1 inline-flex items-center gap-2">
              <GitCompare className="w-4 h-4" /> Comparação de Coprológicas
            </div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">
              Anterior × mais recente
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-sm"
            data-testid="copro-compare-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="py-6 text-center text-xs text-muted-foreground overline">Carregando…</div>
        )}
        {!loading && error && (
          <div className="py-6 text-center text-sm text-destructive" data-testid="compare-error">{error}</div>
        )}
        {!loading && !error && data && !data.current && (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="compare-empty">
            Nenhum exame registrado ainda.
          </div>
        )}
        {!loading && !error && data && data.current && !data.previous && (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="compare-no-previous">
            Este é o único exame do paciente. Sem exame anterior para comparar.
          </div>
        )}

        {!loading && !error && data && data.current && data.previous && (
          <div className="space-y-6">
            {/* Header dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-sm border border-border p-3" data-testid="compare-prev-header">
                <div className="overline text-muted-foreground">Anterior</div>
                <div className="font-heading font-semibold">{fmt(data.previous.exam_date)}</div>
              </div>
              <div className="rounded-sm border border-primary/40 bg-primary/5 p-3" data-testid="compare-cur-header">
                <div className="overline text-primary">Mais recente</div>
                <div className="font-heading font-semibold">{fmt(data.current.exam_date)}</div>
              </div>
            </div>

            {/* Interpretations */}
            <section>
              <div className="overline text-muted-foreground mb-2">Interpretações automáticas</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-xs font-semibold text-emerald-700 mb-2 inline-flex items-center gap-1">
                    <TrendingDown className="w-3.5 h-3.5" /> Sumiram (melhora)
                    <span className="ml-1 text-[10px] opacity-70">
                      {data.diff.interpretations.disappeared.length}
                    </span>
                  </div>
                  {data.diff.interpretations.disappeared.length === 0 ? (
                    <div className="text-xs text-muted-foreground" data-testid="compare-disappeared-empty">
                      Nenhuma.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {data.diff.interpretations.disappeared.map((i) => (
                        <InterpItem key={i.rule_id} interp={i} tone="improved" />
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold text-destructive mb-2 inline-flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" /> Apareceram (piora)
                    <span className="ml-1 text-[10px] opacity-70">
                      {data.diff.interpretations.appeared.length}
                    </span>
                  </div>
                  {data.diff.interpretations.appeared.length === 0 ? (
                    <div className="text-xs text-muted-foreground" data-testid="compare-appeared-empty">
                      Nenhuma.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {data.diff.interpretations.appeared.map((i) => (
                        <InterpItem key={i.rule_id} interp={i} tone="worsened" />
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold text-amber-800 mb-2 inline-flex items-center gap-1">
                    <Minus className="w-3.5 h-3.5" /> Persistiram
                    <span className="ml-1 text-[10px] opacity-70">
                      {data.diff.interpretations.persisted.length}
                    </span>
                  </div>
                  {data.diff.interpretations.persisted.length === 0 ? (
                    <div className="text-xs text-muted-foreground" data-testid="compare-persisted-empty">
                      Nenhuma.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {data.diff.interpretations.persisted.map((i) => (
                        <InterpItem key={i.rule_id} interp={i} tone="persisted" />
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>

            {/* Physical / pH */}
            <section>
              <div className="overline text-muted-foreground mb-2">Análise física</div>
              <table className="w-full text-sm border border-border rounded-sm overflow-hidden">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Campo</th>
                    <th className="text-left px-3 py-2 font-semibold">Anterior</th>
                    <th className="px-2 py-2"></th>
                    <th className="text-left px-3 py-2 font-semibold">Atual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.diff.physical.map((r) => {
                    const better = r.changed && r.previous_unexpected && !r.current_unexpected;
                    const worse = r.changed && !r.previous_unexpected && r.current_unexpected;
                    return (
                      <tr key={r.id} data-testid={`compare-phys-row-${r.id}`}>
                        <td className="px-3 py-2 text-muted-foreground">{r.label}</td>
                        <td className={`px-3 py-2 ${r.previous_unexpected ? "text-destructive font-medium" : ""}`}>
                          {r.previous || "—"}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <ChangeArrow changed={r.changed} worse={worse} better={better} />
                        </td>
                        <td className={`px-3 py-2 ${r.current_unexpected ? "text-destructive font-medium" : ""}`}>
                          {r.current || "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {data.diff.ph && (() => {
                    const r = data.diff.ph;
                    const better = r.changed && r.previous_unexpected && !r.current_unexpected;
                    const worse = r.changed && !r.previous_unexpected && r.current_unexpected;
                    return (
                      <tr data-testid="compare-phys-row-ph">
                        <td className="px-3 py-2 text-muted-foreground">
                          pH <span className="text-[10px]">({r.ref_min}–{r.ref_max})</span>
                        </td>
                        <td className={`px-3 py-2 ${r.previous_unexpected ? "text-destructive font-medium" : ""}`}>
                          {r.previous ?? "—"}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <ChangeArrow changed={r.changed} worse={worse} better={better} />
                        </td>
                        <td className={`px-3 py-2 ${r.current_unexpected ? "text-destructive font-medium" : ""}`}>
                          {r.current ?? "—"}
                        </td>
                      </tr>
                    );
                  })()}
                  {data.diff.physical.length === 0 && !data.diff.ph && (
                    <tr>
                      <td colSpan={4} className="px-3 py-3 text-center text-xs text-muted-foreground">
                        Sem dados de análise física.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            {/* Microscopic */}
            <section>
              <div className="overline text-muted-foreground mb-2">Análise microscópica</div>
              <table className="w-full text-sm border border-border rounded-sm overflow-hidden">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Item</th>
                    <th className="text-left px-3 py-2 font-semibold">Anterior</th>
                    <th className="px-2 py-2"></th>
                    <th className="text-left px-3 py-2 font-semibold">Atual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.diff.microscopic.map((r) => {
                    const better = r.changed && r.previous_unexpected && !r.current_unexpected;
                    const worse = r.changed && !r.previous_unexpected && r.current_unexpected;
                    return (
                      <tr key={r.id} data-testid={`compare-micro-row-${r.id}`}>
                        <td className="px-3 py-2 text-muted-foreground">{r.label}</td>
                        <td className={`px-3 py-2 ${r.previous_unexpected ? "text-destructive font-medium" : ""}`}>
                          {r.previous || "—"}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <ChangeArrow changed={r.changed} worse={worse} better={better} />
                        </td>
                        <td className={`px-3 py-2 ${r.current_unexpected ? "text-destructive font-medium" : ""}`}>
                          {r.current || "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {data.diff.microscopic.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-3 text-center text-xs text-muted-foreground">
                        Sem itens microscópicos preenchidos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────── Section ───────────────────────
export default function PatientCoprologicalSection({ patientId }) {
  const [catalog, setCatalog] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [comparing, setComparing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/patients/${patientId}/coprological-exams`);
      setExams(data || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [patientId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/coprological-catalog");
        setCatalog(data);
      } catch { /* noop */ }
    })();
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (exam) => {
    if (!window.confirm(`Excluir a Prova Coprológica de ${exam.exam_date}?`)) return;
    try {
      await api.delete(`/admin/coprological-exams/${exam.id}`);
      toast.success("Exame excluído.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir.");
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="patient-copro-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline text-muted-foreground mb-1 inline-flex items-center gap-2">
            <FlaskConical className="w-4 h-4" /> Prova Coprológica Funcional
          </div>
          <h3 className="font-heading font-semibold">Achados qualitativos com interpretação automática</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Preenchimento manual do laudo. Regras determinísticas geram sinalizações que também aparecem nos padrões clínicos do PDF de consulta.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setComparing(true)}
            disabled={!catalog || exams.length < 2}
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-40"
            data-testid="copro-compare-btn"
            title={exams.length < 2 ? "É preciso ter ao menos 2 exames para comparar" : "Comparar com exame anterior"}
          >
            <GitCompare className="w-3.5 h-3.5" /> Comparar com anterior
          </button>
          <button
            onClick={() => setCreating(true)}
            disabled={!catalog}
            className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-3 py-2 text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
            data-testid="copro-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Novo exame
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground overline">Carregando…</div>
      ) : exams.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          Nenhuma Prova Coprológica registrada. Clique em &quot;Novo exame&quot; para preencher a partir do laudo.
        </div>
      ) : (
        <div className="space-y-3">
          {exams.map((e) => (
            <CoproExamCard
              key={e.id}
              exam={e}
              catalog={catalog}
              onEdit={(x) => setEditing(x)}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      {creating && catalog && (
        <CoproFormModal
          catalog={catalog}
          onClose={() => setCreating(false)}
          onSaved={async () => { setCreating(false); await load(); }}
          patientId={patientId}
        />
      )}
      {editing && catalog && (
        <CoproFormModal
          catalog={catalog}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
          patientId={patientId}
        />
      )}
      {comparing && (
        <CompareModal patientId={patientId} onClose={() => setComparing(false)} />
      )}
    </div>
  );
}
